import {
  AgentsCard,
  Connector,
  ControlPlane,
  DECISION_CARD_CLASS,
  DecisionApi,
  EDGE,
  EndpointCard,
  ENDPOINTS,
  LogoNode,
} from './atoms';
import { at, drawLine, growFrom, pulse } from './timelineHelpers';
import { useRailsTimeline, type BuildRailsTimeline } from './useRailsTimeline';

const ID = {
  agents: 'rails-m-agents',
  connectA: 'rails-m-connect-a',
  ring: 'rails-m-logo-ring',
  plane: 'rails-m-plane',
  connectDecision: 'rails-m-connect-decision',
  decision: 'rails-m-decision',
  connectEndpoints: 'rails-m-connect-endpoints',
  endpoint: 'rails-m-endpoint',
};

const buildMobileTimeline: BuildRailsTimeline = ({ tl, q, gate }) => {
  // 1. Agents card: outline draws from the top edge and closes at the bottom
  //    centre, where the connector leaves.
  tl.addLabel('agents', 0.5);
  growFrom(tl, ID.agents, 'agents', {
    entry: EDGE.top,
    to: 50,
    duration: 0.6,
  });

  // 2. Vertical connector, lighting the logo ring on the way (the logo
  //    covers ~26%–74% of the line).
  tl.addLabel('connectA', at('agents', 0.6)).fromTo(
    `#${ID.connectA}`,
    { drawSVG: '0% 0%' },
    { drawSVG: '0% 26%', duration: 0.15, ease: 'none' },
    'connectA',
  );
  // Ring: arrives at 12 o'clock (75% round the circle), closes at 6 o'clock
  growFrom(tl, ID.ring, at('connectA', 0.15), {
    entry: 75,
    to: 50,
    duration: 0.35,
  });
  tl.to(
    `#${ID.connectA}`,
    { drawSVG: '0% 100%', duration: 0.15, ease: 'none' },
    at('connectA', 0.5),
  );

  // 3. The line reaches the top of the control plane. Its outline draws down
  //    both sides and closes at the bottom centre, where the decision line
  //    leaves — so here the whole outline is lit before the decision.
  tl.addLabel('plane', at('connectA', 0.65));
  growFrom(tl, ID.plane, 'plane', {
    entry: EDGE.top,
    to: 50,
    duration: 0.6,
  });

  // 4. Decision line draws down and the Decision API lights up fully.
  drawLine(tl, ID.connectDecision, at('plane', 0.6), 0.15);
  growFrom(tl, ID.decision, at('plane', 0.75), {
    entry: EDGE.top,
    to: 50,
    duration: 0.35,
  });

  // 5. Making the decision: the card pulses while lit, then the gate decides.
  //    "No" fades everything out and restarts here.
  tl.addLabel('decide', at('plane', 1.15));
  const pulsed = pulse(tl, q(`.${DECISION_CARD_CLASS}`), 'decide');
  tl.call(gate, undefined, at('decide', pulsed + 0.1));

  // 6. "Yes": continue down through the endpoint cards one after another.
  tl.addLabel('endpoints', at('decide', pulsed + 0.12));
  drawLine(tl, ID.connectEndpoints, 'endpoints', 0.15);
  ENDPOINTS.forEach((_, index) => {
    growFrom(
      tl,
      `${ID.endpoint}-${index}`,
      at('endpoints', 0.15 + index * 0.5),
      {
        entry: EDGE.top,
        to: 50,
        duration: 0.45,
      },
    );
  });
};

export function MobileAnimation() {
  const { container, activeButton } = useRailsTimeline(buildMobileTimeline);

  return (
    <div ref={container} className="flex w-full flex-col items-center">
      <AgentsCard borderId={ID.agents} activeButton={activeButton} />

      <div className="relative flex h-[154px] items-center justify-center">
        <Connector
          id={ID.connectA}
          orientation="vertical"
          className="absolute inset-y-0 left-1/2 h-full -translate-x-1/2"
        />
        <LogoNode ringId={ID.ring} />
      </div>

      {/* The plane keeps its desktop geometry (so the border maths hold) and
          is scaled down to fit narrow viewports. */}
      <div className="h-[166px] w-[343px] s:h-[189px] s:w-[391px] m:h-[231px] m:w-[477px]">
        <div className="origin-top-left scale-[0.72] s:scale-[0.82] m:scale-100">
          <ControlPlane borderId={ID.plane} />
        </div>
      </div>

      <Connector
        id={ID.connectDecision}
        orientation="vertical"
        className="h-[40px]"
      />
      <DecisionApi borderId={ID.decision} />
      <Connector
        id={ID.connectEndpoints}
        orientation="vertical"
        className="h-[40px]"
      />
      <div className="flex flex-col gap-12">
        {ENDPOINTS.map((endpoint, index) => (
          <EndpointCard
            key={endpoint.title}
            borderId={`${ID.endpoint}-${index}`}
            {...endpoint}
          />
        ))}
      </div>
    </div>
  );
}
